import React from 'react'
import { ActivityIndicator, Platform, Pressable, ScrollView, Switch, Text, View } from 'react-native'
import { Redirect, useRouter } from 'expo-router'
import { useSupabaseAuth } from '../src/shared/contexts/SupabaseAuthContext'
import { buildPublicSignInHref } from '../src/signIn/helpers/postAuthRedirect.helper'
import { usePrivacyDataControls } from '../src/privacy/hooks/usePrivacyDataControls'
import { LanguageContext } from '../src/i18n'
import { MarketingPageFooter } from '../components/landing/MarketingPageFooter/MarketingPageFooter'
import { usePublicMarketingWebScrollLayout } from '../src/shared/hooks/usePublicMarketingWebScrollLayout'
import { privacyControlsRouteStyles as styles } from '../appStyles/_privacyControls.styles'

export default function PrivacyControlsRoute() {
  const router = useRouter()
  const languageContext = React.useContext(LanguageContext)
  if (!languageContext) {
    throw new Error('PrivacyControlsRoute must be used within LanguageProvider')
  }
  const { t } = languageContext
  const { isLoaded, isSignedIn } = useSupabaseAuth()
  const {
    isReady,
    marketingEmailOptIn,
    isUpdatingMarketing,
    isExportingData,
    dataExportNextAvailableAt,
    webAnalyticsConsentEnabled,
    updateMarketingConsent,
    requestDataExport,
    updateWebAnalyticsConsent,
  } = usePrivacyDataControls()

  const scrollRef = React.useRef<React.ElementRef<typeof ScrollView>>(null)

  usePublicMarketingWebScrollLayout()

  React.useEffect(() => {
    if (Platform.OS !== 'web') return
    scrollRef.current?.scrollTo({ y: 0, animated: false })
  }, [])

  React.useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return
    const prev = document.body.style.backgroundColor
    document.body.style.backgroundColor = '#1F2937'
    return () => {
      document.body.style.backgroundColor = prev
    }
  }, [])

  if (!isLoaded) return null
  if (!isSignedIn) {
    return <Redirect href={buildPublicSignInHref({ returnUrl: '/privacy-controls' }) as `/${string}`} />
  }

  const privacyControlsContent = (
    <>
      <Pressable
        accessibilityRole="link"
        onPress={() => {
          router.push('/')
        }}
        style={({ pressed, hovered }) => [
          styles.backLink,
          pressed && styles.backLinkPressed,
          hovered && Platform.OS === 'web' && styles.backLinkHovered,
        ]}
      >
        <Text style={styles.backLinkText}>{t.legal.backToHome}</Text>
      </Pressable>

      <Text style={styles.title}>{t.privacyControls.title}</Text>
      <Text style={styles.subtitle}>{t.privacyControls.subtitle}</Text>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t.privacyControls.marketingEmailsTitle}</Text>
        <Text style={styles.sectionHint}>{t.privacyControls.marketingEmailsHint}</Text>
        <View style={styles.rowBetween}>
          <Text style={styles.sectionHint}>
            {marketingEmailOptIn ? t.privacyControls.enabled : t.privacyControls.disabled}
          </Text>
          {isReady ? (
            <Switch
              value={marketingEmailOptIn}
              onValueChange={(nextValue) => {
                void updateMarketingConsent(nextValue)
              }}
              disabled={isUpdatingMarketing}
              trackColor={{ true: '#8B5CF6', false: '#374151' }}
            />
          ) : (
            <ActivityIndicator size="small" />
          )}
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t.privacyControls.analyticsTrackingTitle}</Text>
        <Text style={styles.sectionHint}>{t.privacyControls.analyticsTrackingHint}</Text>
        <View style={styles.rowBetween}>
          <Text style={styles.sectionHint}>
            {webAnalyticsConsentEnabled ? t.privacyControls.enabled : t.privacyControls.disabled}
          </Text>
          <Switch
            value={webAnalyticsConsentEnabled}
            onValueChange={(nextValue) => {
              void updateWebAnalyticsConsent(nextValue)
            }}
            trackColor={{ true: '#8B5CF6', false: '#374151' }}
          />
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t.privacyControls.accountDataCopyTitle}</Text>
        <Text style={styles.sectionHint}>
          {t.privacyControls.accountDataCopyHint}
        </Text>
        <Pressable
          style={({ pressed }) => [
            styles.exportButton,
            (isExportingData || !isReady) && styles.exportButtonDisabled,
            pressed && styles.exportButtonPressed,
          ]}
          onPress={() => {
            void requestDataExport()
          }}
          disabled={isExportingData || !isReady}
        >
          {isExportingData ? (
            <ActivityIndicator size="small" />
          ) : (
            <Text style={styles.exportButtonText}>{t.privacyControls.downloadMyData}</Text>
          )}
        </Pressable>
        {dataExportNextAvailableAt ? (
          <Text style={styles.helperText}>
            {t.privacyControls.nextExportAvailable}: {dataExportNextAvailableAt.toLocaleString()}
          </Text>
        ) : null}
      </View>

      <View style={styles.deleteAccountSection}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t.privacyControls.deleteYourAccount}
          onPress={() => {
            router.push('/app/privacy-and-data')
          }}
          style={({ pressed, hovered }) => [
            styles.deleteAccountRow,
            pressed && styles.deleteAccountRowPressed,
            hovered && Platform.OS === 'web' && styles.deleteAccountRowHovered,
          ]}
        >
          <Text style={styles.deleteAccountRowLabel}>{t.privacyControls.deleteYourAccount}</Text>
          <Text style={styles.deleteAccountRowHint}>{t.privacyControls.deleteYourAccountHint}</Text>
        </Pressable>
      </View>
    </>
  )

  if (Platform.OS === 'web') {
    return (
      <View style={styles.webPageRoot}>
        <View style={styles.webContentRoot}>{privacyControlsContent}</View>
        <MarketingPageFooter contactMode="route" />
      </View>
    )
  }

  return (
    <ScrollView
      ref={scrollRef}
      style={styles.container}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={true}
    >
      <View style={styles.contentColumn}>{privacyControlsContent}</View>
      <MarketingPageFooter contactMode="route" />
    </ScrollView>
  )
}
